/**
 * Secure Storage Utility for API Keys
 *
 * This provides basic obfuscation for API keys stored in localStorage.
 * Note: Browser-only apps cannot truly encrypt without a user password,
 * but we can make it non-obvious to prevent casual exposure.
 */

const STORAGE_KEY = '__app_llm_key';
const PROVIDER_KEY = '__app_llm_provider';

/**
 * Simple base64 encoding with a twist to obfuscate the key
 */
function encode(value: string): string {
  // Add a random salt and encode
  const salt = Math.random().toString(36).substring(7);
  const payload = JSON.stringify({ v: value, s: salt, t: Date.now() });
  return btoa(payload);
}

/**
 * Decode the obfuscated value
 */
function decode(encoded: string): string | null {
  try {
    const payload = JSON.parse(atob(encoded));
    return payload.v || null;
  } catch {
    return null;
  }
}

/**
 * Store API key and provider securely in localStorage
 */
export function storeLLMApiKey(key: string, provider?: 'anthropic' | 'openai' | 'google'): void {
  if (!key || key.trim() === '') {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(PROVIDER_KEY);
    return;
  }
  const encoded = encode(key.trim());
  localStorage.setItem(STORAGE_KEY, encoded);

  // Store provider if specified
  if (provider) {
    localStorage.setItem(PROVIDER_KEY, provider);
  }
}

/**
 * Retrieve API key from localStorage
 */
export function getLLMApiKey(): string | null {
  const encoded = localStorage.getItem(STORAGE_KEY);
  if (!encoded) return null;
  return decode(encoded);
}

/**
 * Check if API key is configured
 */
export function hasLLMApiKey(): boolean {
  return !!getLLMApiKey();
}

/**
 * Clear stored API key and provider
 */
export function clearLLMApiKey(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(PROVIDER_KEY);
}

/**
 * Get stored LLM provider
 */
export function getLLMProvider(): 'anthropic' | 'openai' | 'google' | null {
  const provider = localStorage.getItem(PROVIDER_KEY);
  if (provider === 'anthropic' || provider === 'openai' || provider === 'google') {
    return provider;
  }
  return null;
}

/**
 * Validate API key format (basic checks)
 */
export function validateApiKey(key: string, provider: 'anthropic' | 'openai' | 'google'): { valid: boolean; error?: string } {
  if (!key || key.trim() === '') {
    return { valid: false, error: 'API-nyckel får inte vara tom' };
  }

  const trimmed = key.trim();

  if (provider === 'anthropic') {
    // Anthropic keys start with "sk-ant-"
    if (!trimmed.startsWith('sk-ant-')) {
      return { valid: false, error: 'Anthropic API-nycklar börjar med "sk-ant-"' };
    }
    if (trimmed.length < 20) {
      return { valid: false, error: 'API-nyckeln verkar för kort' };
    }
  } else if (provider === 'openai') {
    // OpenAI keys start with "sk-"
    if (!trimmed.startsWith('sk-')) {
      return { valid: false, error: 'OpenAI API-nycklar börjar med "sk-"' };
    }
    if (trimmed.length < 20) {
      return { valid: false, error: 'API-nyckeln verkar för kort' };
    }
  } else if (provider === 'google') {
    // Google API keys start with "AIza"
    if (!trimmed.startsWith('AIza')) {
      return { valid: false, error: 'Google API-nycklar börjar med "AIza"' };
    }
    if (trimmed.length < 20) {
      return { valid: false, error: 'API-nyckeln verkar för kort' };
    }
  }

  return { valid: true };
}

/**
 * Get masked version of API key for display (shows first 7 and last 4 characters)
 */
export function getMaskedApiKey(): string | null {
  const key = getLLMApiKey();
  if (!key) return null;

  if (key.length < 15) return '***';

  return `${key.substring(0, 7)}...${key.substring(key.length - 4)}`;
}
